/**
 * The one way a time or a date becomes text in CIDER (ADR 0006).
 * Instants are UTC; people read America/Detroit, 12-hour with AM/PM: `7:41 PM`, never `19:41`,
 * never UTC, never a bare offset. ESLint refuses any other formatting anywhere in the repo.
 */

export const DISPLAY_ZONE = 'America/Detroit';

const timeFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: DISPLAY_ZONE,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const dateFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: DISPLAY_ZONE,
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});
const dateYearFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: DISPLAY_ZONE,
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const zoneFmt = new Intl.DateTimeFormat('en-US', { timeZone: DISPLAY_ZONE, timeZoneName: 'short' });
/** The long words a notice uses: `Wednesday, January 13`. Detroit's day, like every other. */
const dayLongFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: DISPLAY_ZONE,
  weekday: 'long',
  month: 'long',
  day: 'numeric',
});

const ymdFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: DISPLAY_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const toDate = (at: Date | string | number): Date => (at instanceof Date ? at : new Date(at));

/**
 * **The date an instant falls on in Detroit, `YYYY-MM-DD`** — the date a person at the festival
 * would write on it. Defaults to now, so `dayOf()` is today.
 *
 * ADR 0006 says a date is Detroit's and never UTC's, and `toISOString().slice(0, 10)` is UTC's:
 * between 8 PM and midnight here it names tomorrow, and on the last evening of a month, next
 * month. This is the browser's side of `FestivalClock.calendarDay`, for the packages that do
 * not carry the domain — and `en-CA` is the one locale that already writes a date this way.
 */
export function dayOf(at: Date | string | number = new Date()): string {
  return ymdFmt.format(toDate(at));
}

/** `7:41 PM` */
export function formatTime(at: Date | string | number): string {
  return timeFmt.format(toDate(at)).replace(' ', ' ');
}

/**
 * `Wednesday, January 13 · 7:00 PM` — a notice's own words, for the meeting notice and the RSVP
 * door. Detroit's day and hour whoever is reading and wherever they are: a chair who opens the
 * notice in another state, or a runner in UTC, reads the evening the meeting is on.
 */
export function formatNoticeWhen(at: Date | string | number): string {
  return `${dayLongFmt.format(toDate(at))} · ${formatTime(at)}`;
}

/** `Sat Sep 12`, or `Sat Sep 12, 2026` with `{ year: true }`. */
export function formatDate(at: Date | string | number, opts: { year?: boolean } = {}): string {
  return (opts.year ? dateYearFmt : dateFmt).format(toDate(at));
}

/** `Sat Sep 12 · 7:41 PM` */
export function formatDateTime(at: Date | string | number, opts: { year?: boolean } = {}): string {
  return `${formatDate(at, opts)} · ${formatTime(at)}`;
}

/** `EDT` or `EST` — only for the rare screen that must say which. */
export function zoneAbbreviation(at: Date | string | number): string {
  return zoneFmt.formatToParts(toDate(at)).find((p) => p.type === 'timeZoneName')?.value ?? '';
}

/**
 * The canvas writes sample times as `10:14a` / `7:41p`. Rendered, they read `10:14 AM` / `7:41 PM`.
 * For seed data only; real instants go through `formatTime`.
 */
export function fromCanvasSample(sample: string): string {
  const m = /^(\d{1,2}):(\d{2})\s*([ap])m?$/i.exec(sample.trim());
  if (!m) throw new Error(`not a canvas time: '${sample}'`);
  return `${Number(m[1])}:${m[2]} ${m[3]?.toUpperCase() === 'A' ? 'AM' : 'PM'}`;
}

/**
 * A calendar date with no time — an entry date, a period's start — written `YYYY-MM-DD`.
 * It is not an instant, so it is not shifted into any zone: `Sep 13, 2025` stays Sep 13.
 */
export function formatCalendarDate(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`not a calendar date: '${ymd}'`);
  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${MONTHS[Number(m[2]) - 1] ?? ''} ${Number(m[3])}, ${m[1]}`;
}

/**
 * A calendar date with the day it falls on — `Fri, Sep 11, 2026` — for a field where the weekday
 * is the point: a festival Friday that is a Thursday should be seen before it is saved. Computed
 * from the date alone, in no zone (a calendar date is not an instant).
 */
export function formatCalendarDay(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`not a calendar date: '${ymd}'`);
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  return `${DAYS[day] ?? ''}, ${formatCalendarDate(ymd)}`;
}

/**
 * A calendar date as a ledger heads its day — `September 13` — the month written out and no year,
 * because the page already says which year it is reading. Computed from the date alone, in no zone.
 */
export function formatLedgerDay(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`not a calendar date: '${ymd}'`);
  const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${MONTHS[Number(m[2]) - 1] ?? ''} ${String(Number(m[3]))}`;
}

/**
 * The count room's day, said the way the room says it — `SATURDAY, Sep 12` — the day's name
 * first, because a festival day is Friday, Saturday or Sunday before it is a date (ADR 0023).
 * Computed from the date alone, in no zone (a calendar date is not an instant).
 */
export function formatCountDay(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`not a calendar date: '${ymd}'`);
  const DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  return `${DAYS[day] ?? ''}, ${MON[Number(m[2]) - 1] ?? ''} ${String(Number(m[3]))}`;
}
